import React, { useMemo } from 'react';
import { Icon, Spinner } from '@amzn/awsui-components-console';
import type { ChatThread } from './types';

interface ChatHistorySidebarProps {
  threads: ChatThread[];
  activeSessionId: string | null;
  loading: boolean;
  onSelectThread: (sessionId: string) => void;
  onDeleteThread: (sessionId: string) => void;
  onClose: () => void;
}

function normalizeUtcDate(isoDate: string): Date {
  const hasOffset = /(?:Z|[+-]\d{2}:\d{2})$/.test(isoDate);
  return new Date(hasOffset ? isoDate : isoDate + 'Z');
}

function formatThreadTime(isoDate: string): string {
  const date = normalizeUtcDate(isoDate);
  const now = new Date();
  const time = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  if (date.toDateString() === now.toDateString()) {
    return time;
  }
  const datePart = date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  return `${datePart}, ${time}`;
}

function groupByAge(threads: ChatThread[]): { label: string; items: ChatThread[] }[] {
  const now = Date.now();
  const day = 86_400_000;
  const groups: Record<string, ChatThread[]> = {
    'Today': [],
    'Yesterday': [],
    'Past 7 Days': [],
    'Past 30 Days': [],
    'Older': [],
  };

  for (const t of threads) {
    const age = now - normalizeUtcDate(t.updatedAt).getTime();
    if (age < day) groups['Today'].push(t);
    else if (age < 2 * day) groups['Yesterday'].push(t);
    else if (age < 7 * day) groups['Past 7 Days'].push(t);
    else if (age < 30 * day) groups['Past 30 Days'].push(t);
    else groups['Older'].push(t);
  }

  return Object.entries(groups)
    .filter(([, items]) => items.length > 0)
    .map(([label, items]) => ({ label, items }));
}

const ChatHistorySidebar: React.FC<ChatHistorySidebarProps> = ({
  threads,
  activeSessionId,
  loading,
  onSelectThread,
  onDeleteThread,
  onClose,
}) => {
  const activeThreads = useMemo(() => threads.filter(t => t.status === 'active'), [threads]);
  const grouped = useMemo(() => groupByAge(activeThreads), [activeThreads]);

  return (
    <div className="chat-history-sidebar">
      <div className="chat-history-sidebar__header">
        <span className="chat-history-sidebar__title">Chat History</span>
        <button
          className="chat-v2-icon-btn"
          onClick={onClose}
          type="button"
          title="Close"
        >
          <Icon name="close" />
        </button>
      </div>

      <div className="chat-history-sidebar__list">
        {loading && (
          <div className="chat-history-sidebar__loading">
            <Spinner />
          </div>
        )}

        {!loading && activeThreads.length === 0 && (
          <div className="chat-history-sidebar__empty">
            No conversations yet
          </div>
        )}

        {!loading &&
          grouped.map(group => (
            <div key={group.label} className="chat-history-group">
              <div className="chat-history-group__label">{group.label}</div>
              {group.items.map(thread => {
                const isActive = thread.sessionId === activeSessionId;
                return (
                  <div
                    key={thread.sessionId}
                    className={`chat-history-item ${isActive ? 'chat-history-item--active' : ''}`}
                  >
                    <button
                      className="chat-history-item__text"
                      onClick={() => onSelectThread(thread.sessionId)}
                      type="button"
                    >
                      <span className="chat-history-item__title">{thread.title || 'Untitled'}</span>
                      <span className="chat-history-item__time">{formatThreadTime(thread.updatedAt)}</span>
                    </button>
                    <button
                      className="chat-history-item__delete"
                      onClick={e => {
                        e.stopPropagation();
                        onDeleteThread(thread.sessionId);
                      }}
                      type="button"
                      title="Delete"
                    >
                      <Icon name="remove" />
                    </button>
                  </div>
                );
              })}
            </div>
          ))}
      </div>
    </div>
  );
};

export default ChatHistorySidebar;
